import React, { useEffect, useMemo, useState } from 'react';
import { Brain, CheckCircle2, Leaf, RotateCcw, Sparkles, Timer, TreePine, Trophy, XCircle } from 'lucide-react';

type GameTab = 'match' | 'quiz' | 'hazard';

const TREE_PAIRS = [
  { id: 'oak', label: 'Oak', icon: '🌳' },
  { id: 'pine', label: 'Pine', icon: '🌲' },
  { id: 'maple', label: 'Maple', icon: '🍁' },
  { id: 'dogwood', label: 'Dogwood', icon: '🌸' },
  { id: 'magnolia', label: 'Magnolia', icon: '🌺' },
  { id: 'cypress', label: 'Cypress', icon: '🌿' },
];

interface QuizQuestion {
  prompt: string;
  options: string[];
  answer: number;
  fact: string;
}

const KID_QUESTIONS: QuizQuestion[] = [
  { prompt: 'Which tree makes acorns?', options: ['Oak', 'Pine', 'Maple', 'Dogwood'], answer: 0, fact: 'Acorns are oak seeds — squirrels and deer depend on them.' },
  { prompt: 'Which tree has needles instead of broad leaves?', options: ['Magnolia', 'Pine', 'Oak', 'Crepe myrtle'], answer: 1, fact: 'Pines are conifers; their needles help them hold water.' },
  { prompt: 'Which tree is famous for red autumn leaves and winged seeds?', options: ['Maple', 'Cypress', 'Dogwood', 'Pine'], answer: 0, fact: 'Maple seeds are samaras — they spin like little helicopters.' },
  { prompt: 'Which tree has smooth, peeling bark and crinkly flowers?', options: ['Oak', 'Crepe myrtle', 'Pine', 'Magnolia'], answer: 1, fact: 'Crepe myrtles are common street trees across the South.' },
  { prompt: 'What do trees release that people and animals need to breathe?', options: ['Oxygen', 'Plastic', 'Salt', 'Smoke'], answer: 0, fact: 'Leaves use sunlight to make food and release oxygen.' },
  { prompt: 'What is the best way to help a newly planted tree?', options: ['Water it deeply and mulch the root zone', 'Tie it tightly to a stake forever', 'Pile mulch against the trunk', 'Cut the top off'], answer: 0, fact: 'Deep watering and a wide mulch ring help roots establish.' },
];

const ADULT_QUESTIONS: QuizQuestion[] = [
  { prompt: 'A tree has opposite branching, winged samaras, and is common in Georgia. Which genus is most likely?', options: ['Acer (maple)', 'Quercus (oak)', 'Pinus (pine)', 'Magnolia'], answer: 0, fact: 'Maples have opposite leaves and paired samaras.' },
  { prompt: 'Which defect is the strongest warning sign of internal decay at the root collar?', options: ['Fungal conk / fruiting body', 'A single dead twig', 'Slight leaf scorch', 'Surface moss'], answer: 0, fact: 'A conk is a fungal fruiting body and warrants a risk assessment.' },
  { prompt: 'What is the target of an ISA-style tree risk assessment?', options: ['What could fail, how likely, and what it could hit', 'The tree’s age only', 'The number of leaves', 'The owner’s opinion'], answer: 0, fact: 'Risk combines likelihood of failure with the target below.' },
  { prompt: 'Which pruning cut is correct?', options: ['Just outside the branch collar', 'Flush to the trunk', 'Leave a long stub', 'Any cut with paint'], answer: 0, fact: 'The branch collar contains the tree’s protective response.' },
  { prompt: 'When is the best time to call 811 before stump grinding?', options: ['Before any digging or grinding', 'After the crew arrives', 'Only for large stumps', 'Never'], answer: 0, fact: 'Utility locates are required before excavation.' },
  { prompt: 'Which practice best protects a tree during construction?', options: ['Fence the critical root zone', 'Park equipment under the canopy', 'Raise the grade around the trunk', 'Store soil against the bark'], answer: 0, fact: 'Compaction and grade changes damage roots.' },
  { prompt: 'What does a soil test primarily tell you?', options: ['pH and nutrient levels', 'Tree height', 'Wind speed', 'Species name'], answer: 0, fact: 'Soil pH affects nutrient availability.' },
  { prompt: 'Which is the safest response to a limb resting on a power line?', options: ['Keep everyone clear and call the utility', 'Pull it down with a rope', 'Cut it from a ladder', 'Spray it with water'], answer: 0, fact: 'Only the utility can safely clear a conductor.' },
];

const HAZARDS = [
  { scenario: 'A dead limb is hanging over the driveway where the family parks.', options: ['Prune or remove it from a safe working position', 'Wait for it to fall', 'Hit it with a stick', 'Park closer'], answer: 0, fact: 'A hanging limb over a target is a hazard; use a qualified crew.' },
  { scenario: 'A tree has a fresh crack at a co-dominant union after a storm.', options: ['Keep people away and get an assessment', 'Climb it to inspect', 'Cut the cracked side off', 'Tie it with rope'], answer: 0, fact: 'Fresh cracks can fail without warning.' },
  { scenario: 'A climber is working within 10 feet of a conductor.', options: ['Stop and call the utility', 'Use a longer pole saw', 'Work faster', 'Ask a groundie to watch'], answer: 0, fact: 'Only qualified line-clearance arborists may work near conductors.' },
  { scenario: 'The chipper is running and a crew member walks behind it.', options: ['Stop the chipper and establish the drop zone', 'Wave them through', 'Rev the engine', 'Keep feeding brush'], answer: 0, fact: 'Chipper entanglement and struck-by incidents are preventable.' },
  { scenario: 'A customer wants a mature oak topped to “keep it small.”', options: ['Explain why topping is harmful and offer reduction or replacement', 'Top it quickly', 'Remove all lower limbs', 'Do it without a written scope'], answer: 0, fact: 'Topping creates weak regrowth and large wounds.' },
  { scenario: 'The ground is saturated and a bucket truck is setting up on a slope.', options: ['Use outriggers on stable mats and reassess', 'Skip the outriggers', 'Park on the slope', 'Work from the bucket without a spotter'], answer: 0, fact: 'Stability and soil bearing capacity are critical on wet slopes.' },
];

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const GameShell: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-[28px] border border-stone-200 p-5 sm:p-7">
    <div className="flex items-start justify-between gap-3 mb-5">
      <div>
        <h3 className="text-lg font-black text-bark">{title}</h3>
        <p className="text-[11px] text-stone-400 mt-1">{subtitle}</p>
      </div>
      <Sparkles className="w-5 h-5 text-gecko shrink-0" />
    </div>
    {children}
  </div>
);

const TreeMatch: React.FC = () => {
  const [cards, setCards] = useState(() => shuffle([...TREE_PAIRS, ...TREE_PAIRS].map((tree, index) => ({ ...tree, uid: `${tree.id}-${index}`, flipped: false, matched: false }))));
  const [flipped, setFlipped] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [best, setBest] = useState<number | null>(() => {
    try { return Number(localStorage.getItem('education_tree_match_best')) || null; } catch { return null; }
  });

  const won = cards.length > 0 && cards.every(card => card.matched);

  useEffect(() => {
    if (won) return;
    const timer = window.setInterval(() => setSeconds(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [won]);

  useEffect(() => {
    if (!won) return;
    if (best === null || moves < best) {
      setBest(moves);
      try { localStorage.setItem('education_tree_match_best', String(moves)); } catch { /* ignore */ }
    }
  }, [won, moves, best]);

  const flip = (index: number) => {
    if (cards[index].flipped || cards[index].matched || flipped.length >= 2) return;
    const next = cards.map((card, i) => (i === index ? { ...card, flipped: true } : card));
    setCards(next);
    const nextFlipped = [...flipped, index];
    setFlipped(nextFlipped);
    if (nextFlipped.length === 2) {
      setMoves(value => value + 1);
      const [a, b] = nextFlipped;
      if (next[a].id === next[b].id) {
        window.setTimeout(() => {
          setCards(current => current.map((card, i) => (i === a || i === b ? { ...card, matched: true } : card)));
          setFlipped([]);
        }, 350);
      } else {
        window.setTimeout(() => {
          setCards(current => current.map((card, i) => (i === a || i === b ? { ...card, flipped: false } : card)));
          setFlipped([]);
        }, 750);
      }
    }
  };

  const reset = () => {
    setCards(shuffle([...TREE_PAIRS, ...TREE_PAIRS].map((tree, index) => ({ ...tree, uid: `${tree.id}-${index}`, flipped: false, matched: false }))));
    setFlipped([]);
    setMoves(0);
    setSeconds(0);
  };

  return (
    <GameShell title="Leaf Match" subtitle="A memory game for young tree lovers — match the pairs.">
      <div className="flex items-center justify-between mb-4 text-[11px] font-black uppercase tracking-widest text-stone-400">
        <span className="inline-flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> {seconds}s</span>
        <span>{moves} moves</span>
        <span className="inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-amber-500" /> {best === null ? '—' : `${best} best`}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
        {cards.map((card, index) => (
          <button
            key={card.uid}
            onClick={() => flip(index)}
            aria-label={card.flipped || card.matched ? card.label : 'Hidden tree card'}
            className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center transition-all ${
              card.matched
                ? 'bg-leaf/20 border-gecko/40'
                : card.flipped
                  ? 'bg-white border-gecko shadow-sm'
                  : 'bg-bark border-bark hover:bg-stone-800'
            }`}
          >
            {card.flipped || card.matched ? (
              <>
                <span className="text-3xl">{card.icon}</span>
                <span className="mt-1 text-[10px] font-black text-bark">{card.label}</span>
              </>
            ) : (
              <Leaf className="w-7 h-7 text-white/50" />
            )}
          </button>
        ))}
      </div>
      {won && (
        <div className="mt-4 rounded-2xl bg-leaf/15 border border-gecko/30 p-4 text-center">
          <p className="text-sm font-black text-bark">All matched! {moves} moves in {seconds}s.</p>
          <button onClick={reset} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gecko text-white text-[10px] font-black uppercase tracking-widest">
            <RotateCcw className="w-3.5 h-3.5" /> Play again
          </button>
        </div>
      )}
    </GameShell>
  );
};

const Quiz: React.FC<{ adult: boolean }> = ({ adult }) => {
  const questions = adult ? ADULT_QUESTIONS : KID_QUESTIONS;
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const question = questions[index];
  const done = index >= questions.length;
  const isCorrect = selected === question?.answer;

  const choose = (option: number) => {
    if (selected !== null) return;
    setSelected(option);
    if (option === question.answer) setScore(value => value + 1);
  };

  const next = () => {
    setSelected(null);
    setIndex(value => value + 1);
  };

  const reset = () => {
    setIndex(0);
    setScore(0);
    setSelected(null);
  };

  if (done) {
    const perfect = score === questions.length;
    return (
      <GameShell title={adult ? 'Tree ID Challenge' : 'Junior Tree Quiz'} subtitle="Every answer comes with a field note.">
        <div className="text-center py-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-leaf/15 flex items-center justify-center">
            <Trophy className={`w-8 h-8 ${perfect ? 'text-amber-500' : 'text-palm'}`} />
          </div>
          <p className="mt-4 text-2xl font-black text-bark">{score} / {questions.length}</p>
          <p className="text-sm text-stone-500 mt-1">{perfect ? 'Perfect score — certified tree nerd.' : 'Good work. Try again and beat your score.'}</p>
          <button onClick={reset} className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gecko text-white text-[10px] font-black uppercase tracking-widest">
            <RotateCcw className="w-3.5 h-3.5" /> Play again
          </button>
        </div>
      </GameShell>
    );
  }

  return (
    <GameShell title={adult ? 'Tree ID Challenge' : 'Junior Tree Quiz'} subtitle="Choose the best answer; read the explanation after each one.">
      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-stone-400 mb-3">
        <span>Question {index + 1} / {questions.length}</span>
        <span>{score} correct</span>
      </div>
      <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden mb-4">
        <div className="h-full bg-gecko transition-all" style={{ width: `${((index) / questions.length) * 100}%` }} />
      </div>
      <p className="text-base font-black text-bark leading-snug">{question.prompt}</p>
      <div className="mt-4 space-y-2">
        {question.options.map((option, optionIndex) => {
          const showCorrect = selected !== null && optionIndex === question.answer;
          const showWrong = selected === optionIndex && optionIndex !== question.answer;
          return (
            <button
              key={option}
              onClick={() => choose(optionIndex)}
              disabled={selected !== null}
              className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-sm font-bold transition ${
                showCorrect
                  ? 'bg-leaf/20 border-gecko text-palm'
                  : showWrong
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-stone-50 border-stone-200 text-bark hover:border-gecko/50 hover:bg-white'
              }`}
            >
              <span>{option}</span>
              {showCorrect && <CheckCircle2 className="w-4 h-4 shrink-0" />}
              {showWrong && <XCircle className="w-4 h-4 shrink-0" />}
            </button>
          );
        })}
      </div>
      {selected !== null && (
        <div className="mt-4 rounded-2xl bg-stone-50 border border-stone-200 p-4">
          <p className="text-xs font-black text-bark">{isCorrect ? 'Nice work.' : 'Field note:'}</p>
          <p className="text-xs text-stone-600 mt-1 leading-relaxed">{question.fact}</p>
          <button onClick={next} className="mt-3 px-4 py-2 rounded-xl bg-bark text-white text-[10px] font-black uppercase tracking-widest">
            {index + 1 === questions.length ? 'See score' : 'Next question'}
          </button>
        </div>
      )}
    </GameShell>
  );
};

const HazardHunter: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const scenario = HAZARDS[index];
  const done = index >= HAZARDS.length;
  const isCorrect = selected === scenario?.answer;

  const choose = (option: number) => {
    if (selected !== null) return;
    setSelected(option);
    if (option === scenario.answer) setScore(value => value + 1);
  };

  const next = () => {
    setSelected(null);
    setIndex(value => value + 1);
  };

  if (done) {
    return (
      <GameShell title="Hazard Hunter" subtitle="A quick field-safety decision game for all ages.">
        <div className="text-center py-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-amber-500" />
          </div>
          <p className="mt-4 text-2xl font-black text-bark">{score} / {HAZARDS.length}</p>
          <p className="text-sm text-stone-500 mt-1">The safest answer is usually the one that stops and reassesses.</p>
          <button onClick={() => { setIndex(0); setScore(0); setSelected(null); }} className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gecko text-white text-[10px] font-black uppercase tracking-widest">
            <RotateCcw className="w-3.5 h-3.5" /> Play again
          </button>
        </div>
      </GameShell>
    );
  }

  return (
    <GameShell title="Hazard Hunter" subtitle="Pick the safest next action. No tree is worth a shortcut.">
      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-stone-400 mb-3">
        <span>Scenario {index + 1} / {HAZARDS.length}</span>
        <span>{score} safe calls</span>
      </div>
      <p className="text-base font-black text-bark leading-snug">{scenario.scenario}</p>
      <div className="mt-4 space-y-2">
        {scenario.options.map((option, optionIndex) => {
          const showCorrect = selected !== null && optionIndex === scenario.answer;
          const showWrong = selected === optionIndex && optionIndex !== scenario.answer;
          return (
            <button
              key={option}
              onClick={() => choose(optionIndex)}
              disabled={selected !== null}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-bold transition ${
                showCorrect
                  ? 'bg-leaf/20 border-gecko text-palm'
                  : showWrong
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-stone-50 border-stone-200 text-bark hover:border-gecko/50 hover:bg-white'
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
      {selected !== null && (
        <div className="mt-4 rounded-2xl bg-stone-50 border border-stone-200 p-4">
          <p className="text-xs font-black text-bark">{isCorrect ? 'Safe call.' : 'Better call:'}</p>
          <p className="text-xs text-stone-600 mt-1 leading-relaxed">{scenario.fact}</p>
          <button onClick={next} className="mt-3 px-4 py-2 rounded-xl bg-bark text-white text-[10px] font-black uppercase tracking-widest">
            {index + 1 === HAZARDS.length ? 'See score' : 'Next scenario'}
          </button>
        </div>
      )}
    </GameShell>
  );
};

const EducationGames: React.FC = () => {
  const [tab, setTab] = useState<GameTab>('match');
  const [quizAdult, setQuizAdult] = useState(false);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {([
          ['match', 'Leaf Match', 'Kids'],
          ['quiz', 'Tree ID Quiz', 'All ages'],
          ['hazard', 'Hazard Hunter', 'All ages'],
        ] as Array<[GameTab, string, string]>).map(([id, label, age]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition ${
              tab === id ? 'bg-bark text-white' : 'bg-white border border-stone-200 text-stone-500 hover:bg-stone-50'
            }`}
          >
            {label} <span className="opacity-60">· {age}</span>
          </button>
        ))}
      </div>
      {tab === 'quiz' && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-stone-400">Difficulty</span>
          <button onClick={() => setQuizAdult(false)} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${!quizAdult ? 'bg-leaf/20 text-palm' : 'bg-stone-100 text-stone-500'}`}>Kids</button>
          <button onClick={() => setQuizAdult(true)} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${quizAdult ? 'bg-leaf/20 text-palm' : 'bg-stone-100 text-stone-500'}`}>Adults</button>
        </div>
      )}
      {tab === 'match' && <TreeMatch />}
      {tab === 'quiz' && <Quiz key={quizAdult ? 'adult' : 'kids'} adult={quizAdult} />}
      {tab === 'hazard' && <HazardHunter />}
    </div>
  );
};

export default EducationGames;
